import CookieIcon from "@mui/icons-material/Cookie";
import { Box, Container, Paper, Typography } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NECESSARY_COOKIES, OPTIONAL_COOKIES } from "../../constants/global";
import { GoogleServicesContext } from "../../context/GoogleServicesContext";
import { gaAction, gaCategory } from "../../utils/analytics";
import { getItem, removeFunctionalItems, setItem } from "../../utils/storage";
import Button, { ButtonVariant } from "./Button";
import ButtonContainer from "./ButtonContainer";
import RenderContent from "./RenderContent";

const CookieConsent = () => {
  const [neccessaryCookies, setNeccessaryCookies] = useState<boolean>(getItem(NECESSARY_COOKIES));

  const {
    setNeccessaryCookiesAllowed,
    setOptionalCookiesAllowed,
    analyticsEvent,
    optionalCookiesAllowed
  } = useContext(GoogleServicesContext);

  const [open, setOpen] = useState<boolean>(!neccessaryCookies);
  const dialogRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const title = t("cookies.title");
  const description = t("cookies.description");
  const allowAll = t("cookies.allowAll");
  const allowNeccessary = t("cookies.allowNeccessary");
  const close = t("cookies.close");

  useEffect(() => {
    if (open && dialogRef.current) {
      const firstButton = dialogRef.current.querySelector("button");
      firstButton?.focus();
    }
  }, [open]);

  return (
    <>
      <Typography
        component="button"
        sx={{
          cursor: "pointer",
          textDecoration: "underline",
          background: "none",
          border: "none",
          color: "inherit",
          font: "inherit",
          padding: 0
        }}
        onClick={() => {
          setOpen(true);
          analyticsEvent(gaCategory.cookies, gaAction.click);
        }}>
        {title}
      </Typography>

      {open && (
        <Box
          ref={dialogRef}
          role="dialog"
          aria-label={title}
          aria-modal="true"
          sx={{
            position: "fixed",
            zIndex: 1000,
            bottom: 0,
            left: 0,
            width: "100%"
          }}>
          <Container maxWidth="xl" sx={{ p: { xs: 0 } }}>
            <Paper
              sx={{
                width: "100%",
                overflowY: "auto",
                boxShadow: "1px -5px 4px 0px rgba(0, 0, 0, 0.25)",
                p: 4,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0
              }}>
              <Typography
                variant="h5"
                fontWeight="bold"
                mb={{ xs: 2, xl: 4 }}
                sx={{ display: "flex", alignItems: "center" }}>
                <CookieIcon fontSize="large" sx={{ mr: 3 }} titleAccess={title} />
                {title}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", xl: "row" },
                  justifyContent: { xs: "center", xl: "space-between" }
                }}>
                <RenderContent content={description} />
                <ButtonContainer sx={{ mt: { xs: 3, xl: 0 }, ml: { xs: 0, xl: 5 } }}>
                  <Button
                    onClick={() => {
                      setItem(NECESSARY_COOKIES, true);
                      setItem(OPTIONAL_COOKIES, true);

                      setNeccessaryCookies(true);
                      setNeccessaryCookiesAllowed(true);
                      setOptionalCookiesAllowed(true);

                      setOpen(false);
                      analyticsEvent(gaCategory.cookies, gaAction.click, "Allow all cookies");
                    }}
                    label={allowAll}
                    variant={ButtonVariant.primary}
                  />
                  <Button
                    onClick={() => {
                      const optionalPreviouslyAllowed = optionalCookiesAllowed;

                      setItem(NECESSARY_COOKIES, true);
                      setItem(OPTIONAL_COOKIES, false);
                      setNeccessaryCookies(true);
                      setNeccessaryCookiesAllowed(true);
                      setOptionalCookiesAllowed(false);

                      removeFunctionalItems();
                      setOpen(false);
                      analyticsEvent(gaCategory.cookies, gaAction.click, "Only necessary cookies");

                      if (optionalPreviouslyAllowed) {
                        window.location.reload();
                      }
                    }}
                    label={allowNeccessary}
                    variant={ButtonVariant.secondary}
                  />
                  {neccessaryCookies && (
                    <Button
                      onClick={() => {
                        setOpen(false);
                      }}
                      label={close}
                      variant={ButtonVariant.secondary}
                    />
                  )}
                </ButtonContainer>
              </Box>
            </Paper>
          </Container>
        </Box>
      )}
    </>
  );
};

export default CookieConsent;
